import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {CommentIcon} from '@primer/octicons-react'
import {ForYouItem} from '../../for-you/shared/ForYouItem'
import {ForYouLeadingIcon} from '../../for-you/shared/ForYouLeadingIcon'
import {getThreadAuthor, ThreadGroupVariantLabels, ThreadGroupVariant, classifyThread} from '../util/classify-thread'
import {getThreadFileLabel} from '../util/thread-file-label'
import {ExpandedThread} from './ExpandedThread'
import {ThreadActions} from './ThreadActions'
import {ThreadTitle} from './ThreadTitle'
import {CompactThread} from './CompactThread'
import {getInitialComment} from '../util/thread-comment-accessors'
import {getCommentSnippet} from '../util/get-comment-snippet'
import {useMemo} from 'react'
import {getCurrentUserLogin} from '@github-ui/client-env'

export function OverviewThread({thread}: {thread: Thread}) {
  const variant = classifyThread(thread, getCurrentUserLogin())
  const ariaLabel = useMemo(() => threadTitleAriaLabel(thread, variant), [thread, variant])

  return (
    <ForYouItem
      key={thread.id}
      itemName={threadItemName(variant)}
      title={<ThreadTitle thread={thread} variant={variant} />}
      ariaLabel={ariaLabel}
      leadingVisual={<ForYouLeadingIcon icon={CommentIcon} />}
      actionSlot={<ThreadActions thread={thread} />}
      revealActionOnHover
      padding="none"
      description={<CompactThread thread={thread} />}
    >
      <ExpandedThread thread={thread} variant={variant} />
    </ForYouItem>
  )
}

function getThreadMessage(thread: Thread | null | undefined, variant: ThreadGroupVariant) {
  if (variant === ThreadGroupVariant.Replied || variant === ThreadGroupVariant.YouReplied) {
    const initialComment = thread ? getInitialComment(thread) : undefined
    if (initialComment) {
      return `replied to "${getCommentSnippet(initialComment.bodyHTML)}"`
    }
  }

  return ThreadGroupVariantLabels[variant] || 'commented'
}

function threadTitleAriaLabel(thread: Thread | null | undefined, variant: ThreadGroupVariant) {
  const message = getThreadMessage(thread, variant)
  // Falls back to the ghost user when the comment author no longer exists.
  const author = getThreadAuthor(thread, variant)

  const fileLabel = thread ? getThreadFileLabel(thread) : null
  if (fileLabel?.hasFile) {
    const lineSuffix = fileLabel.hasLine ? ` ${fileLabel.lineText}` : ''
    return `${author.login} ${message} on ${fileLabel.fileName}${lineSuffix}`
  }

  return `${author.login} ${message}`
}

function threadItemName(variant: ThreadGroupVariant) {
  switch (variant) {
    case ThreadGroupVariant.Replied:
      return 'replied-comment'
    case ThreadGroupVariant.Mentioned:
      return 'mentioned-comment'
    case ThreadGroupVariant.Resolved:
      return 'resolved-comment'
    case ThreadGroupVariant.Commented:
    default:
      return 'commented-comment'
  }
}
